import { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';

import { usePersonalGuaranteeDocument } from './usePersonalGuaranteeDocument';
import { LoadingSpinner } from '../../../../components/LoadingSpinner/LoadingSpinner';
import { isBottomVisible, isTopVisible } from '../../../../utils/scroll';
import { Accordion } from '../../../components/Accordion/Accordion';
import { SubmitButton } from '../../../components/Button/Button';
import { Card } from '../../../components/Card/Card';
import { WhiteLabelOverride } from '../../../components/WhiteLabelOverride/WhiteLabelOverride';
import { Divider } from '../../components/Divider/Divider';
import { useNavigateToNextRequirement } from '../../hooks/useNavigateToNextRequirement';

export const PersonalGuarantee = () => {
  const queryClient = useQueryClient();

  const { goToNextRequirement, nextRequirementName } =
    useNavigateToNextRequirement();
  const [canSign, setCanSign] = useState(false);

  const {
    signPersonalGuaranteeDocument,
    isPersonalGuaranteeSigned,
    isSigningPersonalGuaranetee,
    isLoadingPersonalGuarantee,
  } = usePersonalGuaranteeDocument();

  const isLoading = isSigningPersonalGuaranetee || isLoadingPersonalGuarantee;
  const isDisabled = !isPersonalGuaranteeSigned && (isLoading || !canSign);

  const handleSignDocument = async () => {
    if (!isPersonalGuaranteeSigned) {
      await signPersonalGuaranteeDocument();
      await queryClient.invalidateQueries({
        queryKey: ['fetchGetLoanDocumentsRequiredIwocapay'],
      });
    }

    goToNextRequirement();
  };

  const getSignButtonContent = () => {
    if (isLoading) {
      return <LoadingSpinner />;
    }

    if (isPersonalGuaranteeSigned) {
      return `Continue to ${nextRequirementName}`;
    }

    return 'Sign personal guarantee';
  };

  const onScrollBottom = () => {
    setCanSign(true);
  };

  return (
    <Card title="Sign your Personal Guarantee">
      <p className="text-m font-reg">
        Please read the entire agreement and click 'Sign personal guarantee' to
        continue.
      </p>
      <Accordion title="Why am I doing this?" className="mb-xl">
        We ask for this commitment because we offer unsecured credit, which
        means it's not backed by any assets. Once a director of the business
        signs a personal guarantee it makes them personally responsible for
        repaying the loan.
      </Accordion>
      <Divider />
      <PersonalGuaranteeDocument onScrollBottom={onScrollBottom} />
      <p>
        *You <b>must</b> scroll down through the entire document to continue.
      </p>
      <WhiteLabelOverride componentType="button" disabled={isDisabled}>
        <SubmitButton onClick={handleSignDocument} disabled={isDisabled}>
          {getSignButtonContent()}
        </SubmitButton>
      </WhiteLabelOverride>
    </Card>
  );
};

type TScrollState = 'top' | 'in-between' | 'bottom';
const PersonalGuaranteeDocument = ({
  onScrollBottom,
}: {
  onScrollBottom: () => void;
}) => {
  const { personalGuaranteeContent } = usePersonalGuaranteeDocument();
  const [scrollState, setScrollState] = useState<TScrollState>('top');

  const handleScroll = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    const top = isTopVisible(e.target as HTMLDivElement);
    const bottom = isBottomVisible(e.target as HTMLDivElement);

    if (top) {
      setScrollState('top');
    }

    if (bottom) {
      setScrollState('bottom');
      onScrollBottom();
    }

    if (scrollState === 'in-between') return;

    setScrollState('in-between');
  };

  const documentWrapperBase =
    'max-h-[200px] min-h-[200px] m800:max-h-[320px] m800:min-h-[320px] overflow-y-auto border-b border-secondary-80 mb-l px-s pb-s';

  if (!personalGuaranteeContent)
    return <div className={documentWrapperBase}>Loading...</div>;

  return (
    <div
      data-testid="pg-document-wrapper"
      onScroll={handleScroll}
      className={clsx(documentWrapperBase, 'fs-exclude', {
        'bg-scroll-fade-top': scrollState === 'top',
        'bg-scroll-fade-both': scrollState === 'in-between',
        'bg-scroll-fade-bottom': scrollState === 'bottom',
      })}
      dangerouslySetInnerHTML={{ __html: personalGuaranteeContent }}
    />
  );
};
